import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getPatientDetailsPath } from '../utils/patientNavigation';

const initialState = {
  name: '',
  cpf: '',
  phone: '',
  zipCode: '',
  address: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  birthDate: '',
  gender: '',
  height: '',
  weight: ''
};

const statesBR = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

const maskCpf = (digits) => {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const isValidCpf = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;

  const calculateDigit = (base) => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base.charAt(i), 10) * (base.length + 1 - i);
    }
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };

  const firstDigit = calculateDigit(digits.slice(0, 9));
  const secondDigit = calculateDigit(digits.slice(0, 10));
  return firstDigit === Number(digits[9]) && secondDigit === Number(digits[10]);
};

export default function PacienteNovo() {
  const navigate = useNavigate();
  const { onAddPatient } = useOutletContext();
  const [form, setForm] = useState(initialState);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState('');
  const [cpfError, setCpfError] = useState('');

  const toTitleCase = (str) =>
    str.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  const handleChange = (event) => {
    const { name, value } = event.target;
    const normalized = name === 'name' ? toTitleCase(value) : value;
    setForm((prev) => ({ ...prev, [name]: normalized }));
  };

  const handleZipBlur = async () => {
    const raw = (form.zipCode || '').replace(/\D/g, '');
    setZipError('');
    if (raw.length !== 8) {
      if (raw.length > 0) setZipError('CEP deve ter 8 dígitos.');
      return;
    }

    try {
      setZipLoading(true);
      const response = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
      const data = await response.json();
      if (data.erro) {
        setZipError('CEP não encontrado.');
        return;
      }

      setForm((prev) => ({
        ...prev,
        address: data.logradouro || prev.address,
        district: data.bairro || prev.district,
        city: data.localidade || prev.city,
        state: data.uf || prev.state
      }));
    } catch (error) {
      setZipError('Não foi possível buscar o CEP.');
    } finally {
      setZipLoading(false);
    }
  };

  const handleCpfChange = (event) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 11);
    const masked = maskCpf(digits);
    setForm((prev) => ({ ...prev, cpf: masked }));

    if (!digits) {
      setCpfError('');
      return;
    }

    if (digits.length === 11 && !isValidCpf(digits)) {
      setCpfError('CPF inválido.');
    } else {
      setCpfError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const digits = (form.cpf || '').replace(/\D/g, '');
    if (digits.length !== 11 || !isValidCpf(digits)) {
      setCpfError('CPF inválido.');
      return;
    }
    try {
      const savedPatient = onAddPatient ? await onAddPatient(form) : form;
      setForm(initialState);
      navigate(getPatientDetailsPath(savedPatient?._id), { state: savedPatient });
    } catch (err) {
      console.error('Erro ao salvar paciente:', err);
      alert('Erro ao salvar paciente. Tente novamente.');
    }
  };

  return (
    <div className="patients-card">
      <div className="patients-content">
        <div className="patients-header">
          <div className="patients-title">
            <h1>Novo paciente</h1>
            <p className="welcome-text">Preencha os dados abaixo para cadastrar o paciente.</p>
          </div>
        </div>
        <form className="patient-form" onSubmit={handleSubmit}>
          <section className="form-card">
            <h2>Dados principais</h2>
            <div className="form-grid">
              <label>
                Nome completo
                <input className="mac-input" name="name" value={form.name} onChange={handleChange} required />
              </label>
              <label>
                CPF
                <input
                  className="mac-input"
                  name="cpf"
                  value={form.cpf}
                  onChange={handleCpfChange}
                  placeholder="000.000.000-00"
                  required
                />
                {cpfError && <span className="error-text">{cpfError}</span>}
              </label>
              <label>
                Telefone
                <input
                  className="mac-input"
                  name="phone"
                  value={form.phone}
                  onChange={(e) => {
                    let digits = e.target.value.replace(/\D/g, '').slice(0, 11);

                    if (!digits) {
                      handleChange({ target: { name: 'phone', value: '' } });
                      return;
                    }

                    // Formato: (11) 98765-4321 ou (11) 2345-6789
                    let masked = digits;
                    if (digits.length <= 2) {
                      masked = `(${digits}`;
                    } else if (digits.length <= 6) {
                      masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
                    } else if (digits.length <= 10) {
                      masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
                    } else {
                      masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
                    }

                    handleChange({ target: { name: 'phone', value: masked } });
                  }}
                  placeholder=""
                />
              </label>
              <label>
                CEP
                <input
                  className="mac-input"
                  name="zipCode"
                  value={form.zipCode}
                  onChange={(e) => {
                    // máscara simples: 12345-678
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
                    handleChange({ target: { name: 'zipCode', value: masked } });
                  }}
                  onBlur={handleZipBlur}
                  placeholder="Digite o CEP"
                />
                {zipLoading && <span className="helper-text">Buscando endereço...</span>}
                {zipError && <span className="error-text">{zipError}</span>}
              </label>
              <label>
                Endereço
                <input className="mac-input" name="address" value={form.address} onChange={handleChange} />
              </label>
              <label>
                Número
                <input className="mac-input" name="number" value={form.number} onChange={handleChange} />
              </label>
              <label>
                Complemento
                <input className="mac-input" name="complement" value={form.complement} onChange={handleChange} />
              </label>
              <label>
                Bairro
                <input className="mac-input" name="district" value={form.district} onChange={handleChange} />
              </label>
              <label>
                Cidade
                <input className="mac-input" name="city" value={form.city} onChange={handleChange} />
              </label>
              <label>
                Estado
                <select className="mac-input" name="state" value={form.state} onChange={handleChange}>
                  <option value="">Selecione</option>
                  {statesBR.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>
          <section className="form-card">
            <h2>Dados complementares</h2>
            <div className="form-grid">
              <label>
                Data de Nascimento
                <input
                  className="mac-input"
                  name="birthDate"
                  type="date"
                  value={form.birthDate}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Sexo
                <select className="mac-input" name="gender" value={form.gender} onChange={handleChange}>
                  <option value="">Selecione</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Feminino">Feminino</option>
                </select>
              </label>
              <label>
                Altura (cm)
                <input
                  className="mac-input"
                  name="height"
                  type="number"
                  value={form.height}
                  onChange={handleChange}
                  placeholder="170 cm"
                />
              </label>
            </div>
          </section>
          <div className="form-actions">
            <button type="button" className="ghost-button" onClick={() => navigate('/pacientes')}>
              Cancelar
            </button>
            <button type="submit" className="mac-button">
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
